/**
 * Helper functions for building chat-related state objects in the app layout.
 */
import { noop } from '@saaga/shared/utils/noop.util.ts'

import type { UseDeleteConversationReturn } from '@/hooks/use-delete-conversation.hook'
import type { UsePinConversationReturn } from '@/hooks/use-pin-conversation.hook'
import type { UseRenameConversationReturn } from '@/hooks/use-rename-conversation.hook'
import type { ConversationPinStateMap } from './sidebar-conversations.helper'
import type { ChatItemActions, ChatItemEditState } from './sidebar-item-actions.interface'
import type { ChatDeleteDialogState } from './use-app-layout-actions.hook'

export interface BuildChatActionsParams {
  renameConversation: UseRenameConversationReturn
  deleteConversation: UseDeleteConversationReturn
  pinConversation: UsePinConversationReturn
  conversationPinStateMap: ConversationPinStateMap
}

export const buildChatActions = ({
  renameConversation,
  deleteConversation,
  pinConversation,
  conversationPinStateMap,
}: BuildChatActionsParams): ChatItemActions => ({
  onShare: noop,
  onRename: renameConversation.startEditing,
  onRenameSave: (_id: string, newName: string) => {
    renameConversation.handleSave(newName)
  },
  onRenameCancel: renameConversation.cancelEditing,
  onPin: (chatId: string) => {
    const isCurrentlyPinned = conversationPinStateMap[chatId] ?? false
    pinConversation.togglePin(chatId, isCurrentlyPinned)
  },
  onArchive: noop,
  onDelete: deleteConversation.startDeleting,
})

export const buildChatDeleteDialog = (
  deleteConversation: UseDeleteConversationReturn
): ChatDeleteDialogState => ({
  deletingConversationId: deleteConversation.deletingConversationId,
  isDeleting: deleteConversation.isDeleting,
  onConfirm: deleteConversation.confirmDelete,
  onCancel: deleteConversation.cancelDeleting,
})

export const buildChatEditState = (
  renameConversation: UseRenameConversationReturn
): ChatItemEditState => ({
  editingId: renameConversation.editingConversationId,
  isRenaming: renameConversation.isRenaming,
})
